'use client'

import Image, { type ImageProps } from 'next/image'
import { useEffect, useRef, useState } from 'react'

type RetryableImageProps = Omit<ImageProps, 'onError' | 'src'> & {
  src: string
  /** CDN / lazy race için yeniden deneme (varsayılan 2) */
  maxRetries?: number
}

/**
 * next/image + native lazy: çok sayıda CDN isteğinde Chrome isteği iptal edip
 * naturalWidth=0 bırakabiliyor; URL aslında 200. Hata olunca eager + cache-bust retry.
 */
export default function RetryableImage({
  src,
  maxRetries = 2,
  loading,
  priority,
  ...rest
}: RetryableImageProps) {
  const [attempt, setAttempt] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    setAttempt(0)
  }, [src])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const resolvedSrc =
    attempt > 0 ? `${src}${src.includes('?') ? '&' : '?'}_r=${attempt}` : src

  // LCP / above-fold: priority → eager; retry sonrası da lazy kalmasın
  const effectiveLoading: ImageProps['loading'] =
    priority || attempt > 0 ? 'eager' : loading ?? 'lazy'

  return (
    <Image
      key={`${resolvedSrc}:${attempt}`}
      src={resolvedSrc}
      priority={priority}
      loading={effectiveLoading}
      onError={() => {
        if (attempt >= maxRetries) return
        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => {
          setAttempt((a) => a + 1)
        }, 150 * (attempt + 1))
      }}
      {...rest}
    />
  )
}
